(ns dre.widgets.events
  (:require
   [day8.re-frame.tracing :refer-macros [fn-traced]]
   [dre.pages.search.events :as search-events]
   [dre.storage.events :as-alias storage]
   [dre.widgets.authors.events :as e-authors]
   [dre.widgets.clinical-studies.events :as e-clinical-studies]
   [dre.widgets.data-sources.events :as e-data-sources]
   [dre.widgets.dates.events]
   [dre.widgets.db :as db]
   [dre.widgets.demographics.events]
   [dre.widgets.geography.events :as e-geography]
   [dre.widgets.publication-type.events :as e-publication-type]
   [dre.widgets.related-concepts.events]
   [dre.widgets.top-terms.events :as e-top-terms]
   [dre.widgets.analysis.tag-distribution]
   [dre.widgets.burden-of-disease.events]
   [dre.widgets.question-answering.events]
   [re-frame.core :as rf]))


(def db-path :widgets)

(defn distint-init-fn
  [items]
  (map first (vals (group-by :init-fx items))))

(defn phases
  [active]
  (->> (db/registry)
       (filter #(contains? active (:id %)))
       distint-init-fn
       (sort-by :priority)
       (partition-by :priority)
       (map (partial map :id))))

(defn widgets-to-load
  [active]
  (->> (db/registry)
       (filter #(contains? active (:id %)))))

(def default-active
  {:diagnosis #{e-data-sources/id e-publication-type/id e-top-terms/id}
   :efficacy-safety #{e-data-sources/id e-publication-type/id e-clinical-studies/id e-top-terms/id}
   :healthcare-economics #{e-data-sources/id e-publication-type/id e-top-terms/id}
   :prevalence #{e-data-sources/id e-publication-type/id e-geography/id e-top-terms/id}
   :risk-prognosis #{e-data-sources/id e-publication-type/id e-top-terms/id}
   :treatment-patterns #{e-data-sources/id e-publication-type/id e-top-terms/id}
   :default #{e-data-sources/id e-publication-type/id e-top-terms/id e-authors/id}})


;; -- Events

(rf/reg-event-fx
  ::init
  [(rf/path db-path) (rf/inject-cofx ::storage/get :active-widgets-by-viewtype) (rf/inject-cofx ::storage/get :personalized-widgets-layouts) rf/trim-v]
  (fn-traced
   [{:keys [db active-widgets-by-viewtype personalized-widgets-layouts]} [view-type]]
   (let [view-type (or view-type :default)
         active (get (merge default-active active-widgets-by-viewtype) view-type #{})]
     {:db (assoc db
                 :extra (cond
                          (= :diagnosis view-type)
                          (assoc (:extra db) :top-terms {:child-option {:id "DIA"}})
                          :else
                          nil)
                 :view-type view-type
                 :active-widgets-by-viewtype active-widgets-by-viewtype
                 :active active
                 :layouts (js->clj personalized-widgets-layouts))})))


(rf/reg-event-fx
 ::fetch
 [(rf/path db-path) rf/trim-v]
 (fn-traced
  [{:keys [db]} _]
  (let [active (:active db)
        phases-index (into {} (map-indexed vector) (phases active))]
    {:db (assoc db
                :phases phases-index)
     :fx (conj
          (mapv (fn [w] [:dispatch [(:set-loading-fx w)]]) (widgets-to-load active))
          [:dispatch [::execute-phase 0]])})))

(rf/reg-event-fx
 ::fetch-selected
 [(rf/path db-path) rf/trim-v]
 (fn-traced
  [{:keys [db]} [selected-ids]]
  (let [active (set selected-ids)
        phases-index (into {} (map-indexed vector) (phases active))]
    {:db (assoc db
                :phases phases-index)
     :dispatch [::execute-phase 0]})))

(rf/reg-event-fx
  ::execute-phase
  [rf/trim-v]
  (fn
    [{db :db} [phase-n]]
    (let [search-db (get db search-events/db-path)
          search-query (:search-query search-db)
          last-selected-filter-key (:last-selected-filter-key search-db)
          widget-filters (:widget-filters search-db)
          aq (partial search-events/analysis-query search-query widget-filters)
          aq* (comp second aq)
          widgets-db (get db db-path)
          phase-widgets (get-in widgets-db [:phases phase-n])]
      {:fx (map (fn [widget-id]
                  (when-let [init (get-in (db/index) [widget-id :init-fx])]
                    [:dispatch [init {:after-fx [::after-fx phase-n widget-id]
                                      :pills-api-params-fn aq*
                                      :search-query search-query
                                      :last-selected-filter-key last-selected-filter-key
                                      :extra (:extra widgets-db)}]]))
                phase-widgets)})))


(rf/reg-event-fx
  ::after-fx
  [(rf/path db-path) rf/trim-v]
  (fn-traced
    [{db :db} [phase-n widget-id]]
    (let [current-phase (get-in db [:phases phase-n])
          updated-phase (remove #{widget-id} current-phase)
          next-phase-n (inc phase-n)]
      {:db (assoc-in db [:phases phase-n] updated-phase)
       :fx [(when (and (empty? updated-phase)
                       (contains? (:phases db) next-phase-n))
              [:dispatch [::execute-phase next-phase-n]])]})))


(rf/reg-event-fx
 ::toggle-active
 [(rf/path db-path) rf/trim-v]
 (fn-traced
  [{db :db} [id opts]]
  (let [active (:active db)
        active-widgets-by-viewtype (:active-widgets-by-viewtype db)
        view-type (:view-type db)
        exist? (active id)
        active (if exist?
                 (disj active id)
                 (conj active id))
        last-selected-widget (if exist?
                               nil
                               id)
        active-widgets-by-viewtype-new (assoc active-widgets-by-viewtype view-type active)] 
    (-> {:db (assoc db
                    :active active
                    :active-widgets-by-viewtype active-widgets-by-viewtype-new
                    :last-selected-widget last-selected-widget
                    :extra (assoc (:extra db) id opts))
         ::storage/set [:active-widgets-by-viewtype active-widgets-by-viewtype-new]}
        (cond-> (not exist?) (assoc :dispatch [::fetch-selected [id]]))))))

(rf/reg-event-fx
 ::sync-personalized-widgets-layouts
 [(rf/path db-path) rf/trim-v]
 (fn-traced
  [{db :db} [layouts-type all all-personalized-widgets-layouts]]
  (let [layouts (assoc all-personalized-widgets-layouts layouts-type all)]
    {:db (-> db
          (assoc :layouts layouts)
          (assoc :last-selected-widget nil))
     ::storage/set [:personalized-widgets-layouts (clj->js layouts)]})))

(rf/reg-event-fx
 ::reset-widgets-layout
 [(rf/path db-path) rf/trim-v]
 (fn-traced
  [{db :db} _]
  (let [layouts {}
        active-widgets-by-viewtype {}
        active (get (merge default-active active-widgets-by-viewtype) (:view-type db) #{})]
    {:db (assoc db :layouts layouts :active-widgets-by-viewtype active-widgets-by-viewtype :active active)
         :fx [[::storage/set [:personalized-widgets-layouts (clj->js layouts)]]
              [::storage/set [:active-widgets-by-viewtype active-widgets-by-viewtype]]]})))

 

;; -- Subscriptions

(rf/reg-sub
  ::state
  (fn [db _]
    (db-path db)))


(rf/reg-sub
  ::loading?
  :<- [::state]
  (fn [db]
    (seq (apply concat (vals (:phases db))))))


(rf/reg-sub
  ::widget
  :<- [::state]
  (fn [db [_ id]]
    (get db id)))

(rf/reg-sub
 ::view-type
 :<- [::state]
 (fn [db _]
   (:view-type db :default)))

(rf/reg-sub
  ::active
  :<- [::state]
  (fn [db _]
    (or (:active db) #{})))

(rf/reg-sub
 ::last-selected-widget
 :<- [::state]
 (fn [db _]
   (get db :last-selected-widget nil)))

(rf/reg-sub
 ::layouts
 :<- [::state]
 (fn [db _]
   (:layouts db {})))


